import React, { useState, useEffect } from "react";

interface WidgetGalleryProps {
  onInstall?: (registryKey: string) => void;
  onRemove?: (registryKey: string) => void;
  installed?: { [key: string]: boolean };
}

// List of installable components (add more as desired)
const components = [
  {
    name: "Scratchpad",
    description: "A simple notes widget for quick thoughts.",
    filename: "Scratchpad.tsx",
    icon: "\u{1F4DD}",
    registryKey: "Scratchpad",
  },
  {
    name: "Quick To-do List",
    description: "A minimalist to-do list for quick tasks.",
    filename: "QuickTodo.tsx",
    icon: "\u2713",
    registryKey: "Quick To-do List",
  },
  {
    name: "SearchWidget",
    description: "Quickly search from your dashboard.",
    filename: "SearchWidget.tsx",
    icon: "\u{1F50D}",
    registryKey: "SearchWidget",
  },
  {
    name: "NPR Hourly News",
    description: "Listen to the latest 5-minute NPR news update.",
    filename: "NPRWidget.tsx",
    icon: "\u{1F399}",
    registryKey: "NPR Hourly News",
  },
  {
    name: "Events On This Day",
    description: "Daily history game — guess when events happened.",
    filename: "EventsOnThisDayCard.tsx",
    icon: "\u{1F4C5}",
    registryKey: "DatesWiki",
  },
  // NASA Picture of the Day temporarily removed due to feed/image issues
];

const WidgetGallery: React.FC<WidgetGalleryProps> = ({ onInstall, installed = {}, onRemove }) => {
  const [isSingleCol, setIsSingleCol] = useState(false);

  useEffect(() => {
    function handleResize() {
      setIsSingleCol(window.innerWidth < 640); // Tailwind sm: 640px
    }
    handleResize();
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // Reorder: installed apps first
  const sorted = [
    ...components.filter((c) => installed[c.registryKey]),
    ...components.filter((c) => !installed[c.registryKey]),
  ];
  return (
    <div className="flex flex-col gap-4 min-h-[100px]">
      <div className={`grid grid-cols-1 sm:grid-cols-2 gap-4 ${isSingleCol ? "px-1" : ""}`}>
        {sorted.map((comp) => {
          const isInstalled = !!installed[comp.registryKey];
          return (
            <div
              key={comp.name}
              className={`rounded-[var(--radius-md)] border border-[var(--color-border)] p-4 transition-colors
                bg-[var(--color-bg-secondary)]
                hover:bg-[var(--color-bg-hover)]
                ${isInstalled ? "ring-2 ring-[var(--color-accent)]" : ""}`}
            >
              <span className="text-xl">{comp.icon}</span>
              <div className="flex-1">
                <div className="font-medium text-[var(--color-text-primary)] mb-1">{comp.name}</div>
                <div className="text-sm text-[var(--color-text-tertiary)] mb-2">
                  {comp.description}
                </div>
                {isInstalled ? (
                  <button
                    className="mt-2 px-3 py-1 rounded bg-[var(--color-bg-hover)] text-[var(--color-text-tertiary)] text-xs font-medium transition-colors hover:bg-[color-mix(in_srgb,var(--color-danger)_10%,transparent)] hover:text-[var(--color-danger)] border border-transparent"
                    onClick={() => {
                      if (onRemove) {
                        onRemove(comp.registryKey);
                      }
                    }}
                  >
                    Remove
                  </button>
                ) : (
                  <button
                    className="mt-2 px-3 py-1 rounded bg-[var(--color-accent)] hover:bg-[var(--color-accent)] text-[var(--color-text-inverse)] text-xs font-medium transition-colors"
                    onClick={() => {
                      if (onInstall) {
                        onInstall(comp.registryKey);
                      }
                    }}
                  >
                    Install
                  </button>
                )}
              </div>
            </div>
          );
        })}
      </div>
      {/* Use a plain <style> tag, not style jsx, for compatibility */}
      <style>{`
        @keyframes installed-bounce {
          0% { transform: scale(1); }
          30% { transform: scale(1.08); }
          50% { transform: scale(0.96); }
          70% { transform: scale(1.04); }
          100% { transform: scale(1); }
        }
        .animate-fade-in {
          animation: fade-in 0.7s;
        }
        @keyframes fade-in {
          from { opacity: 0; }
          to { opacity: 1; }
        }
      `}</style>
    </div>
  );
};

export default WidgetGallery;
